import { format } from 'date-fns';

export function currentDateTimeHumanReadable(): string {
  const now = new Date();

  // Extract individual components of the date and time
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-indexed
  const day = now.getDate().toString().padStart(2, "0");
  const hours = now.getHours().toString().padStart(2, "0");
  const minutes = now.getMinutes().toString().padStart(2, "0");
  const seconds = now.getSeconds().toString().padStart(2, "0");
  const milliseconds = now.getMilliseconds().toString().padStart(3, "0");
  const centiseconds = milliseconds.substring(0, 2); // Get the first two digits of milliseconds

  // Format the date and time in a human-readable format
  const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${centiseconds}`;

  return formattedDateTime;
}

export function nowMs(): number {
  return new Date().getTime();
}

export function formatMs(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  } else {
    return `${(ms / 1000).toFixed(1)}s`;
  }
}

// Calculates the time since the start time and returns formatted result
export function timeSince(start: number): string {
  const now = Date.now();
  const elapsed = now - start;
  return formatMs(elapsed);
}

export const formatTimeIntervalReadable = (seconds: number) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts: string[] = [];
  if (days > 0) parts.push(`${days} d`);
  if (hours > 0) parts.push(`${hours} h`);
  if (minutes > 0) parts.push(`${minutes} m`);

  return parts.length > 0 ? parts.join(', ') : '0 m';
};

/**
 * Format a date as "Jan 23" (Month Day without year)
 */
export function formatDateMonthDay(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'MMM d');
}

/**
 * Calculate the number of business days (weekdays) between two dates
 * Counts actual hours that occur on business days and divides by 24
 * @param date1 - Start date
 * @param date2 - End date
 * @returns Number of business days between dates (negative if date2 is before date1)
 */
export function businessDaysBetween(date1: Date, date2: Date): number {
  const start = new Date(date1);
  const end = new Date(date2);

  // Determine direction
  const isReverse = end < start;
  const [earlierDate, laterDate] = isReverse ? [end, start] : [start, end];

  let totalHours = 0;

  // Iterate day by day
  let currentDay = new Date(earlierDate);
  currentDay.setHours(0, 0, 0, 0);

  while (currentDay < laterDate) {
    const dayOfWeek = currentDay.getDay();

    // Only count weekdays (Monday=1 to Friday=5)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      // Calculate start and end of the current day within our range
      const dayStart = currentDay.getTime() > earlierDate.getTime() ? currentDay : earlierDate;

      const nextDay = new Date(currentDay);
      nextDay.setDate(nextDay.getDate() + 1);
      const dayEnd = nextDay.getTime() < laterDate.getTime() ? nextDay : laterDate;

      const hoursThisDay = (dayEnd.getTime() - dayStart.getTime()) / (1000 * 60 * 60);
      totalHours += hoursThisDay;
    }

    currentDay.setDate(currentDay.getDate() + 1);
  }

  const days = totalHours / 24;
  return isReverse ? -days : days;
}

/**
 * Add business days to a date by adding business hours (days * 24)
 * Skips weekends entirely - weekend hours don't count toward business days
 * @param date - Starting date
 * @param days - Number of business days to add (can be fractional, negative to subtract)
 * @returns New date with business days added
 */
export function addBusinessDays(date: Date, days: number): Date {
  const result = new Date(date);
  const isNegative = days < 0;
  const targetBusinessHours = Math.abs(days) * 24;
  const hourIncrement = isNegative ? -1 : 1;

  let businessHoursAdded = 0;

  while (businessHoursAdded < targetBusinessHours) {
    // Move one hour in the specified direction
    result.setTime(result.getTime() + hourIncrement * 60 * 60 * 1000);

    const dayOfWeek = result.getDay();

    // Only count hours that fall on weekdays (Monday=1 to Friday=5)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessHoursAdded++;
    }
    // Weekend hours are added to the calendar but don't count as business hours
  }

  return result;
}
